import {
  AdminInput,
  BTCategoryResponse,
  Button,
  Div,
  CreateBTMenuCategoryDto,
  PropsModal,
  UpdateBTMenuCategoryDto,
  ModalPortal,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';

export const AddCategoryModal = ({
  addModal,
  closeModal,
  handleAddCategory,
  handleEditCategory,
  edit,
}: PropsModal & {
  addModal: boolean | BTCategoryResponse | null;
  handleAddCategory?: (
    name: CreateBTMenuCategoryDto['name'],
    description: CreateBTMenuCategoryDto['description'],
    emptyForm: () => void
  ) => void;
  handleEditCategory?: (
    name: UpdateBTMenuCategoryDto['name'],
    description: UpdateBTMenuCategoryDto['description'],
    minItems: UpdateBTMenuCategoryDto['minItems'],
    maxItems: UpdateBTMenuCategoryDto['maxItems'],
    emptyForm: () => void
  ) => void;
  edit?: boolean;
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [minItems, setMinItems] = useState<number | null>(null);
  const [maxItems, setMaxItems] = useState<number | null>(null);
  const [items, setItems] = useState<number | null>(null);

  const data = addModal;

  useEffect(() => {
    if (edit && typeof data === 'object') {
      setName(data?.name || '');
      setDescription(data?.description || '');
      setMinItems(data?.minItems || null);
      setMaxItems(data?.maxItems || null);
      setItems(data?.items.length || null);
    } else {
      emptyForm();
    }
  }, [data]);

  const emptyForm = () => {
    setName('');
    setDescription('');
    setMinItems(null);
    setMaxItems(null);
    setItems(null);
  };

  const handleSubmit = () => {
    if (edit) {
      handleEditCategory?.(name, description, minItems, maxItems, emptyForm);
    } else {
      handleAddCategory?.(name, description, emptyForm);
    }
  };

  const hasItems = items !== null;

  return (
    <ModalPortal
      isOpen={!!addModal}
      closeModal={closeModal}
      header={edit ? 'Edit Category' : 'Add Category'}
      logo={false}
      footer={
        <>
          <Button $selected $w="100%" onClick={handleSubmit}>
            Confirm
          </Button>
          <Button
            $mt="16"
            $selected
            $gray
            $w="100%"
            onClick={() => closeModal?.()}
          >
            Cancel
          </Button>
        </>
      }
      $numOfFloatingButtons={2}
    >
      <Div $flex $column $gap="24px">
        <AdminInput
          placeholder="Enter here"
          label="Category Name"
          value={name}
          onChange={({ target }: { target: HTMLInputElement }) =>
            setName(target.value)
          }
        />

        <AdminInput
          placeholder="Enter here"
          label="Description"
          type="textarea"
          value={description}
          onChange={({ target }: { target: HTMLInputElement }) =>
            setDescription(target.value)
          }
          $textareaHeight={200}
        />

        {edit && (
          <Div $flex $gap="16px" $alignCenter>
            <AdminInput
              placeholder="Enter here"
              label="Minimum Items"
              value={minItems ?? ''}
              onChange={({ target }: { target: HTMLInputElement }) => {
                const value = target.value.replace(/[^0-9]/g, '');
                setMinItems(value === '' ? null : Number(value));
              }}
              $textCenter
              tooltip={`The minimum number of items that customer must select in this category. ${
                !hasItems
                  ? `<br/><b>This will be enabled once you add items in this category.<b/>`
                  : ''
              }`}
              disabled={!hasItems}
              tooltipWidth="300px"
              tooltipWidthSM="200px"
            />

            <AdminInput
              placeholder="Enter here"
              label="Maximum Items"
              value={maxItems ?? ''}
              onChange={({ target }: { target: HTMLInputElement }) => {
                const value = target.value.replace(/[^0-9]/g, '');
                setMaxItems(value === '' ? null : Number(value));
              }}
              $textCenter
              tooltip={`The maximum number of items that customer can select in this category. ${
                !hasItems
                  ? `<br/><b>This will be enabled once you add items in this category.<b/>`
                  : ''
              }`}
              disabled={!hasItems}
              tooltipWidth="300px"
              tooltipWidthSM="200px"
            />
          </Div>
        )}
      </Div>
    </ModalPortal>
  );
};
